/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   warehousesService.ts
 *     Feature         :   117-operational-dashboards
 *     Description     :   API client and React Query hook for the warehouse table list
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 *     Modified        :   2026-11-03 Spec 297 T036: the schema-changes, schema-aggregation and per-warehouse
 *                         breakdown hooks removed with hooks/useWarehouses.ts, their only caller, which nothing
 *                         imported; the table list (WarehouseDetailPage) is what remains
 * =========================================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../apiClient';
import type { WarehouseTable, PaginationMeta } from '../../types/operations';

export const OPERATIONS_WAREHOUSE_KEYS = {
  all: ['operations', 'warehouses'] as const,
  tables: (filters: Record<string, any>) => [...OPERATIONS_WAREHOUSE_KEYS.all, 'tables', filters] as const,
};

export interface TablesResponse {
  data: WarehouseTable[];
  pagination: PaginationMeta;
}

export async function getWarehouseTables(filters: {
  search?: string; staleOnly?: boolean; warehouseId?: string; page?: number; pageSize?: number;
}): Promise<TablesResponse> {
  const response = await apiClient.get('/api/operations/warehouses/tables', { params: filters });
  return response.data.data;
}

export function useWarehouseTables(filters: { search?: string; staleOnly?: boolean; warehouseId?: string; page?: number; pageSize?: number; }) {
  return useQuery({ queryKey: OPERATIONS_WAREHOUSE_KEYS.tables(filters), queryFn: () => getWarehouseTables(filters), staleTime: 30_000, refetchInterval: 60_000 });
}
